import React, { Component } from "react";
import {
	getConversationHeaders,
	getCultureCode,
	getTotalNumberOfConversations,
} from "../../api/secure-messaging-api";
import ThreeDotsLoadingSpinner from "../../components/three-dots-loading-spinner";
import translate from "../../constants/translations";
import type { ConversationHeader, CustomerContext } from "../../types";
import ConversationExpander from "./conversation-expander";

interface ConversationListProps {
	customer: CustomerContext;
	numberOfConversationsPerLoad: number;
	isSecureMessagingRedesignEnabled?: boolean;
	emptyListMessage?: string;
}

interface ConversationListState {
	cultureCode: string;
	conversationHeaders: ConversationHeader[];
	numberOfConversationsLoaded: number;
	totalNumberOfConversations: number;
	allLoaded: boolean;
	loading: boolean;
}

class ConversationList extends Component<
	ConversationListProps,
	ConversationListState
> {
	private mounted = false;

	private loadMoreButton = React.createRef<HTMLButtonElement>();

	constructor(props: ConversationListProps) {
		super(props);

		this.onLoadMoreClick = this.onLoadMoreClick.bind(this);

		this.state = {
			cultureCode: props.customer.cultureCode || "en-GB",
			conversationHeaders: [],
			numberOfConversationsLoaded: 0,
			totalNumberOfConversations: 0,
			allLoaded: true,
			loading: true,
		};
	}

	componentDidMount() {
		const { customer } = this.props;
		this.mounted = true;

		getCultureCode(customer).then((cultureCode) => {
			if (this.mounted && cultureCode) {
				this.setState({ cultureCode });
			}
		});

		this.getConversationCount();
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	private t(term: string): string {
		return translate(this.state.cultureCode, term);
	}

	private onLoadMoreClick() {
		this.loadMoreConversations().then(() => this.scrollToLoadMoreButton());
	}

	private getConversationCount() {
		const { customer } = this.props;

		return getTotalNumberOfConversations(customer).then(
			(totalNumberOfConversations) => {
				if (this.mounted) {
					this.setState({ totalNumberOfConversations });
					this.loadMoreConversations(totalNumberOfConversations);
				}
			},
		);
	}

	private loadMoreConversations(max?: number) {
		const { customer, numberOfConversationsPerLoad } = this.props;
		const {
			numberOfConversationsLoaded: numberOfConversationsToSkip,
			totalNumberOfConversations,
		} = this.state;
		this.setState({ loading: true });

		return getConversationHeaders(
			customer,
			numberOfConversationsToSkip,
			numberOfConversationsPerLoad,
		).then((conversationHeaders) => {
			if (this.mounted) {
				const numberOfConversationsLoaded =
					this.state.numberOfConversationsLoaded + conversationHeaders.length;
				this.setState({
					conversationHeaders:
						this.state.conversationHeaders.concat(conversationHeaders),
					numberOfConversationsLoaded,
					loading: false,
					allLoaded:
						numberOfConversationsLoaded >= (max ?? totalNumberOfConversations),
				});
			}
		});
	}

	private scrollToLoadMoreButton() {
		if (this.loadMoreButton.current) {
			const scrolledIntoToView = this.loadMoreButton.current;
			const boundingClientRect = scrolledIntoToView.getBoundingClientRect();
			const centerOfTheWindow =
				boundingClientRect.top + window.pageYOffset - window.innerHeight / 2;
			window.scrollTo({ top: centerOfTheWindow, behavior: "smooth" });
		}
	}

	render() {
		const {
			customer,
			isSecureMessagingRedesignEnabled = false,
			emptyListMessage = "",
		} = this.props;

		const {
			cultureCode,
			allLoaded,
			conversationHeaders,
			totalNumberOfConversations,
			loading,
		} = this.state;

		const showEmptyListMessage = isSecureMessagingRedesignEnabled
			? totalNumberOfConversations === 0 && !loading
			: false;

		return (
			<div className="mypages-offers-messages">
				{showEmptyListMessage ? (
					<div className="if text center u-margin-top--40 u-margin-bottom--40">
						{emptyListMessage}
					</div>
				) : (
					<React.Fragment>
						<div className="if panels secure-messaging-component__conversation-list">
							{conversationHeaders.map((conversationHeader) => (
								<ConversationExpander
									key={conversationHeader.conversationId}
									conversationId={conversationHeader.conversationId}
									subject={conversationHeader.subject}
									activityDate={conversationHeader.activityDate}
									isClosed={conversationHeader.isClosed}
									hasOneOrMoreUnseenMessages={
										conversationHeader.hasOneOrMoreUnseenMessages
									}
									customer={customer}
									cultureCode={cultureCode}
									claim={conversationHeader.claim as { id: string } | undefined}
								/>
							))}
						</div>
						{loading ? (
							<div className="if u-justify-center">
								<ThreeDotsLoadingSpinner />
							</div>
						) : (
							!allLoaded && (
								<div className="if u-margin-top--small u-margin-bottom--small myPages-text-centered">
									<button
										ref={this.loadMoreButton}
										id="load-more"
										type="button"
										onClick={this.onLoadMoreClick}
										className="if button secondary"
									>
										{this.t("LoadMore")}
									</button>
								</div>
							)
						)}
					</React.Fragment>
				)}
			</div>
		);
	}
}

export default ConversationList;
